/**
 * PropertyCard - Mobile
 * 
 * Property list item with thumbnail, address, key metrics, and status.
 * Clean, professional presentation aligned with land stewardship aesthetic.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, touch, icon, radius } from '../../../shared/tokens/mobile';
import { PropertyStatus } from '../../../shared/types/property';

interface PropertyCardProps {
  address: string;
  location?: string;
  acreage?: number | null;
  askingPrice?: number | null;
  status: PropertyStatus;
  onPress: () => void;
  hideBorder?: boolean;
}

// Status display configuration
const STATUS_CONFIG: Record<PropertyStatus, { label: string; color: string; bgColor: string }> = {
  research: { label: 'Researching', color: color.warning, bgColor: color.warningLight },
  under_contract: { label: 'Under Contract', color: color.info, bgColor: color.infoLight },
  owned: { label: 'Owned', color: color.success, bgColor: color.successLight },
  listed_for_sale: { label: 'For Sale', color: color.warning, bgColor: color.warningLight },
  sold: { label: 'Sold', color: color.textMuted, bgColor: color.backgroundSecondary },
};

export function PropertyCard({
  address,
  location,
  acreage,
  askingPrice,
  status,
  onPress,
  hideBorder = false,
}: PropertyCardProps) {
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.research;
  
  // Format metrics
  const acreageText = acreage ? `${acreage.toFixed(1)} ac` : null;
  const priceText = formatPrice(askingPrice);
  const metricsText = [acreageText, priceText].filter(Boolean).join(' • ');

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.card,
        !hideBorder && styles.border,
        pressed && styles.pressed,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${address}, ${statusConfig.label}`}
    >
      {/* Thumbnail placeholder */}
      <View style={styles.thumbnail}>
        <Ionicons name="image-outline" size={icon.lg} color={color.textMuted} />
      </View>

      {/* Content */}
      <View style={styles.content}>
        <Text style={styles.address} numberOfLines={1}>{address}</Text>
        {location && (
          <Text style={styles.location} numberOfLines={1}>{location}</Text>
        )}
        {metricsText && (
          <Text style={styles.metrics} numberOfLines={1}>{metricsText}</Text>
        )}
        <View style={[styles.statusBadge, { backgroundColor: statusConfig.bgColor }]}>
          <View style={[styles.statusDot, { backgroundColor: statusConfig.color }]} />
          <Text style={[styles.statusText, { color: statusConfig.color }]}>
            {statusConfig.label}
          </Text>
        </View>
      </View>

      {/* Chevron */}
      <Ionicons name="chevron-forward" size={icon.sm + 2} color={color.textMuted} />
    </Pressable>
  );
}

function formatPrice(value: number | null | undefined): string | null {
  if (!value) return null;
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M asking`;
  }
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}K asking`;
  }
  return `$${value.toLocaleString()} asking`;
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: space.sm + space.xs,
    paddingHorizontal: space.md,
    gap: space.sm + space.xs,
    backgroundColor: color.surface,
  },
  border: {
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  pressed: {
    backgroundColor: color.pressed,
  },
  thumbnail: {
    width: 64,
    height: 64,
    borderRadius: radius.sm,
    backgroundColor: color.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: color.border,
  },
  content: {
    flex: 1,
    gap: 2,
  },
  address: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.textPrimary,
  },
  location: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    lineHeight: text.caption.lineHeight,
    color: color.textSecondary,
  },
  metrics: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    lineHeight: text.caption.lineHeight,
    color: color.textSecondary,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: space.sm,
    paddingVertical: 3,
    borderRadius: radius.full,
    marginTop: space.xs,
    gap: space.xs,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
});

